import {
  ArticleCopyImprovement,
  ArticleHeadingRewrite,
  ArticleNewSectionSuggestion,
  ArticleRewriteResult
} from '@/common/api/params/articleDiagnosis'
import { ArticlePartRecord, ArticleProposalDetail } from '@/common/model/media/article'

// AI案の手動修正（「AI案を修正」テキストフィールドの状態）
export interface ArticleRewriteEdit {
  text: string // テキストフィールドの現在値（初期値は編集元リライト案のテキスト）
  adopt: boolean // 「反映に取り入れる」チェック時に手動修正案を採用する
}

export interface ArticleRewriteSelection {
  titleIndex: number | null
  metaIndex: number | null
  headingIndexes: Set<number>
  sectionIndexes: Set<number>
  copyIndexes: Set<number>
  // 手動修正（AI案を修正）。単一選択のタイトル/メタはグループ単位、
  // 複数選択の見出し/追加/コピーは案ごと（index キー）に保持する。
  titleEdit: ArticleRewriteEdit | null
  metaEdit: ArticleRewriteEdit | null
  headingEdits: Map<number, ArticleRewriteEdit>
  sectionEdits: Map<number, ArticleRewriteEdit>
  copyEdits: Map<number, ArticleRewriteEdit>
}

export const EMPTY_REWRITE_SELECTION: ArticleRewriteSelection = {
  titleIndex: null,
  metaIndex: null,
  headingIndexes: new Set(),
  sectionIndexes: new Set(),
  copyIndexes: new Set(),
  titleEdit: null,
  metaEdit: null,
  headingEdits: new Map(),
  sectionEdits: new Map(),
  copyEdits: new Map()
}

const anyEditAdopted = (edits: Map<number, ArticleRewriteEdit>): boolean =>
  Array.from(edits.values()).some((e) => e.adopt)

export const hasAnySelection = (selection: ArticleRewriteSelection): boolean =>
  selection.titleIndex != null ||
  selection.metaIndex != null ||
  selection.headingIndexes.size > 0 ||
  selection.sectionIndexes.size > 0 ||
  selection.copyIndexes.size > 0 ||
  !!selection.titleEdit?.adopt ||
  !!selection.metaEdit?.adopt ||
  anyEditAdopted(selection.headingEdits) ||
  anyEditAdopted(selection.sectionEdits) ||
  anyEditAdopted(selection.copyEdits)

const findPart = (parts: ArticlePartRecord[], partType: string): ArticlePartRecord | undefined =>
  parts.find((p) => p.part_type === partType)

// 反映先のコンテンツエリア。AIが target_area を返さない/不正な場合は上部として扱う（従来挙動）
type AreaKey = 'top' | 'bottom'

const targetAreaOf = (item: { target_area?: string }): AreaKey =>
  item.target_area === 'content_area_bottom' ? 'bottom' : 'top'

const otherArea = (area: AreaKey): AreaKey => (area === 'top' ? 'bottom' : 'top')

interface AreaHtml {
  top: string
  bottom: string
}

// 文字列置換の反映：指定エリアに該当文字列が無ければもう一方のエリアも探す。
// どちらにも無ければ何も置換されない（本文を壊すより無反映を選ぶ）。
const replaceInArea = (
  areas: AreaHtml,
  area: AreaKey,
  search: string,
  replacement: string
): AreaHtml => {
  const target = areas[area].includes(search)
    ? area
    : areas[otherArea(area)].includes(search)
      ? otherArea(area)
      : null
  if (!target) return areas
  return { ...areas, [target]: areas[target].split(search).join(replacement) }
}

// 見出しリライトの反映：本文中の該当箇所（原文そのまま）をリライト後の文言へ置換する
const applyHeadingRewrite = (areas: AreaHtml, item: ArticleHeadingRewrite): AreaHtml =>
  item.original ? replaceInArea(areas, targetAreaOf(item), item.original, item.rewritten) : areas

// コピー改善の反映：問題箇所の原文をそのまま改善案へ置換する
const applyCopyImprovement = (areas: AreaHtml, item: ArticleCopyImprovement): AreaHtml =>
  item.before_hint && item.after_example
    ? replaceInArea(areas, targetAreaOf(item), item.before_hint, item.after_example)
    : areas

// 新規セクション提案の反映：AIが型に沿って生成したセクションHTMLを、反映先エリアの末尾へそのまま追記する
// （型が未登録のサイトでは見出し＋段落の素のHTMLがそのまま入る）
const appendNewSection = (areas: AreaHtml, item: ArticleNewSectionSuggestion): AreaHtml => {
  if (!item.section_html) return areas
  const area = targetAreaOf(item)
  return { ...areas, [area]: `${areas[area]}\n${item.section_html}` }
}

export interface AppliedArticleRewriteFields {
  title: string
  metaDescription: string
  contentAreaTop: string
  contentAreaBottom: string
}

// 選択されたリライト案のみを、現在の記事内容（コミット済みの最新版）へマージしてフォーム反映用の値を組み立てる
// （保存は行わず、値を返すのみ。呼び出し側で setValue する）
// 手動修正（AI案を修正）が採用されている場合は、AI案の文言ではなく修正後の文言を用いる。
export const buildAppliedRewriteFields = (
  detail: ArticleProposalDetail,
  rewrite: ArticleRewriteResult,
  selection: ArticleRewriteSelection
): AppliedArticleRewriteFields => {
  const latest = detail.latest_article

  const title = selection.titleEdit?.adopt
    ? selection.titleEdit.text
    : selection.titleIndex != null
      ? (rewrite.title_rewrites?.[selection.titleIndex]?.rewritten ?? latest.title ?? '')
      : (latest.title ?? '')
  const metaDescription = selection.metaEdit?.adopt
    ? selection.metaEdit.text
    : selection.metaIndex != null
      ? (rewrite.meta_description_rewrites?.[selection.metaIndex]?.rewritten ??
        latest.meta_description ??
        '')
      : (latest.meta_description ?? '')

  let areas: AreaHtml = {
    top: findPart(detail.parts, 'content_area_top')?.content_html ?? '',
    bottom: findPart(detail.parts, 'content_area_bottom')?.content_html ?? ''
  }
  ;(rewrite.heading_rewrites ?? []).forEach((item, idx) => {
    const edit = selection.headingEdits.get(idx)
    if (!selection.headingIndexes.has(idx) && !edit?.adopt) return
    const rewritten = edit?.adopt ? edit.text : item.rewritten
    areas = applyHeadingRewrite(areas, { ...item, rewritten })
  })
  ;(rewrite.copy_improvements ?? []).forEach((item, idx) => {
    const edit = selection.copyEdits.get(idx)
    if (!selection.copyIndexes.has(idx) && !edit?.adopt) return
    const after_example = edit?.adopt ? edit.text : item.after_example
    areas = applyCopyImprovement(areas, { ...item, after_example })
  })
  ;(rewrite.new_sections ?? []).forEach((item, idx) => {
    const edit = selection.sectionEdits.get(idx)
    if (!selection.sectionIndexes.has(idx) && !edit?.adopt) return
    // 手動修正は見出し文言に対する修正なので、セクションHTML中の見出しを差し替えて反映する
    const section_html =
      edit?.adopt && item.heading && item.section_html
        ? item.section_html.split(item.heading).join(edit.text)
        : item.section_html
    areas = appendNewSection(areas, { ...item, section_html })
  })

  return {
    title,
    metaDescription,
    contentAreaTop: areas.top,
    contentAreaBottom: areas.bottom
  }
}
